import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AxiosService } from "../../services/axios/axiosService";
import { APP_TITLE, getStatusDisplayLabel } from "../../constant";
import { format } from "date-fns";
import { formatDateTimeShortIst } from "../../utils/dateFormatUtils";
const safeStr = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

const toDateOrNull = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

const formatDateShort = (value) => {
  const d = toDateOrNull(value);
  return d ? format(d, "MMM dd, yyyy") : safeStr(value) || "-";
};

const formatDateTimeShort = (value) => {
  const d = toDateOrNull(value);
  return d ? format(d, "MMM dd, yyyy • hh:mm a") : safeStr(value) || "-";
};

const getCandidateKey = (row) => {
  return (
    row?.id ??
    row?.candidateId ??
    row?.Candidate_ID ??
    row?.candidateID ??
    row?.candidateEmail ??
    row?.email ??
    ""
  );
};

const CandidateProfileDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const candidateKey = safeStr(params?.candidateKey).trim();
  const stateCandidate = location?.state?.candidate ?? null;

  const [loading, setLoading] = useState(false);
  const [candidate, setCandidate] = useState(stateCandidate);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (candidate) return;
      if (!candidateKey) return;
      try {
        setLoading(true);
        const res = await AxiosService.get("/api/candidate");
        const list = res?.data ?? res?.candidates ?? res;
        const arr = Array.isArray(list) ? list : [];
        const found = arr.find((r) => safeStr(getCandidateKey(r)) === candidateKey);
        if (mounted) setCandidate(found ?? null);
      } catch {
        if (mounted) setCandidate(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [candidate, candidateKey]);

  const name = candidate?.candidateName ?? candidate?.fullName ?? "-";
  const email = candidate?.candidateEmail ?? candidate?.email ?? "-";
  const phone = candidate?.candidatePhone ?? candidate?.contactNumber ?? candidate?.Contact_Number ?? "-";
  const locationText = candidate?.location ?? candidate?.Location ?? "-";
  const experienceText =
    candidate?.experienceYears != null
      ? `${candidate.experienceYears} years`
      : candidate?.experience ?? candidate?.OverallExperience ?? "-";
  const appliedDate = candidate?.appliedDate ?? candidate?.createdAt ?? "-";
  const profileSource = candidate?.zohoCandidateId ? "Zoho Recruit (Auto)" : "By Scout";
  const jobTitle = candidate?.positionTitle ?? candidate?.jobTitle ?? "-";
  const jobId = candidate?.positionId ?? candidate?.jobPostId ?? candidate?.JobOpeningId ?? "-";
  const source = candidate?.source ?? "Zoho";
  const screeningStatus = candidate?.status ?? "Completed";
  const interviewStatus = candidate?.interviewStatus ?? "Scheduled";
  const score = candidate?.overallScore ?? candidate?.score ?? 87;
  const resumeName = candidate?.resumeFileName ?? "resume.pdf";
  const resumeUrl = candidate?.resumeUrl ?? candidate?.resumeURL ?? candidate?.resume ?? candidate?.resume_link ?? "";
  const statusChipColor = useMemo(() => {
    const s = safeStr(screeningStatus).toLowerCase();
    if (s.includes("complete")) return "success";
    if (s.includes("progress")) return "warning";
    if (s.includes("schedule")) return "info";
    return "default";
  }, [screeningStatus]);

  const onDownloadResume = () => {
    const u = safeStr(resumeUrl).trim();
    if (!u) return;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Helmet>
        <title>Candidate Profile | {APP_TITLE}</title>
      </Helmet>

      <Box sx={{ p: { xs: 2, sm: 3.5 }, backgroundColor: theme.palette.background.default, minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 1240, mx: "auto" }}>
          <Button
            startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none", mb: 2, color: theme.palette.primary.main }}
          >
            Back to Candidate List
          </Button>

        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            backgroundColor: theme.palette.background.paper,
            boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 999,
                  display: "grid",
                  placeItems: "center",
                  background: "linear-gradient(135deg, rgba(37,99,235,0.16), rgba(99,102,241,0.10))",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <PersonOutlineRoundedIcon color="primary" />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  {name}
                </Typography>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 0.65, flexWrap: "wrap" }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      Applied {formatDateShort(appliedDate)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PlaceOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {safeStr(locationText)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {safeStr(experienceText)} exp.
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.25} sx={{ width: { xs: "100%", md: "auto" } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Edit Profile
              </Button>
              <Button
                fullWidth
                variant="contained"
                endIcon={<OpenInNewRoundedIcon />}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Open in Zoho
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {[
                { label: "Source", value: source },
                { label: "Interview Status", value: getStatusDisplayLabel(interviewStatus) },
                {
                  label: "Screening Status",
                  value: (
                    <Chip
                      size="small"
                      label={safeStr(screeningStatus)}
                      color={statusChipColor}
                      sx={{ fontWeight: 750 }}
                    />
                  ),
                },
                {
                  label: "Overall Score",
                  value: (
                    <Typography fontWeight={800} color="success.main">
                      {safeStr(score)}%
                    </Typography>
                  ),
                },
              ].map((item) => (
                <Grid key={item.label} item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.75,
                      py: 1.35,
                      borderRadius: 2.5,
                      border: `1px solid ${theme.palette.divider}`,
                      background: "linear-gradient(180deg, rgba(148,163,184,0.06), rgba(148,163,184,0.03))",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={750}>
                      {item.label}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {typeof item.value === "string" ? (
                        <Typography fontWeight={750}>{item.value}</Typography>
                      ) : (
                        item.value
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography color="text.secondary" fontWeight={700}>
              Loading profile…
            </Typography>
          </Paper>
        ) : !candidate ? (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography fontWeight={800}>Candidate not found</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Please go back to the candidate list and open the profile again.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: { xs: 2, sm: 2.5 },
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 750, mb: 1.5 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { icon: <EmailOutlinedIcon />, label: "Email Address", value: email },
                    { icon: <PhoneOutlinedIcon />, label: "Phone Number", value: phone },
                    { icon: <PlaceOutlinedIcon />, label: "Location", value: locationText },
                    { icon: <DescriptionOutlinedIcon />, label: "Overall Experience", value: experienceText },
                  ].map((item) => (
                    <Grid key={item.label} item xs={12} sm={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              background: "linear-gradient(135deg, rgba(37,99,235,0.16), rgba(99,102,241,0.10))",
                              color: theme.palette.primary.main,
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={850}>
                              {item.label}
                            </Typography>
                            <Typography fontWeight={800} sx={{ mt: 0.25 }} noWrap>
                              {safeStr(item.value)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: { xs: 2, sm: 2.5 },
                  backgroundColor: theme.palette.background.paper,
                  mt: 3,
                  boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 750, mb: 1.5 }}>
                  Job Application Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>
                      Position Applied For
                    </Typography>
                    <Typography fontWeight={750} sx={{ mt: 0.25 }}>
                      {safeStr(jobTitle)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>
                      Job ID
                    </Typography>
                    <Typography fontWeight={750} sx={{ mt: 0.25 }}>
                      {safeStr(jobId)}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={800}>
                  Job Description
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                  {safeStr(candidate?.jobDescription) ||
                    "We are looking for a strong candidate with experience in modern frontend development and cloud-ready systems."}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: { xs: 2, sm: 2.5 },
                  backgroundColor: theme.palette.background.paper,
                  mt: 3,
                  boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1.5}
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Resume
                  </Typography>
                  <Button variant="contained" sx={{ textTransform: "none", borderRadius: 2 }}>
                    View Screening Results
                  </Button>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "rgba(34, 197, 94, 0.10)",
                        display: "grid",
                        placeItems: "center",
                        color: theme.palette.success.main,
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={850} noWrap title={safeStr(resumeName)}>
                        {safeStr(resumeName)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={650}>
                        Uploaded on {formatDateShort(appliedDate)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={onDownloadResume}
                    disabled={!safeStr(resumeUrl).trim()}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Download
                  </Button>
                </Paper>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: { xs: 2, sm: 2.5 },
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 750, mb: 1.5 }}>
                  Profile Timeline
                </Typography>

                <Stack spacing={2}>
                  {[
                    {
                      title: "Profile Created",
                      inlineMeta: profileSource,
                      icon: <CheckCircleOutlineRoundedIcon />,
                      color: theme.palette.primary.main,
                      meta: "",
                      time: formatDateTimeShort(candidate?.createdAt ?? appliedDate),
                    },
                    {
                      title: "Resume Uploaded",
                      icon: <DescriptionOutlinedIcon />,
                      color: theme.palette.success.main,
                      meta: safeStr(resumeName),
                      time: formatDateTimeShort(candidate?.createdAt ?? appliedDate),
                    },
                    // {
                    //   title: "Screening Completed",
                    //   icon: <CheckCircleOutlineRoundedIcon />,
                    //   color: theme.palette.success.main,
                    //   meta: `Score: ${safeStr(score)}%`,
                    //   time: safeStr(candidate?.screeningCompletedAt ?? appliedDate),
                    // },
                    {
                      title: "Interview Scheduled",
                      icon: <EventAvailableRoundedIcon />,
                      color: theme.palette.warning.main,
                      meta: candidate?.interviewScheduledDate ? "Scheduled" : "-",
                      time: formatDateTimeShort(candidate?.interviewScheduledDate),
                    },
                    ...(candidate?.interviewCompletedDate
                      ? [
                          {
                            title: "Interview Completed",
                            icon: <CheckCircleOutlineRoundedIcon />,
                            color: theme.palette.success.main,
                            meta: "Interview finished",
                            time: formatDateTimeShortIst(candidate?.interviewCompletedDate),
                          },
                        ]
                      : []),
                  ].map((item) => (
                    <Stack key={item.title} direction="row" spacing={1.25} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(148,163,184,0.10)",
                          color: item.color,
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.85} alignItems="baseline" flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={850} component="span">
                            {item.title}
                          </Typography>
                          {!!safeStr(item.inlineMeta).trim() && (
                            <Typography variant="caption"  color="text.secondary" component="span">
                              {item.inlineMeta}
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" fontWeight={650} sx={{ display: "block" }}>
                          {safeStr(item.time)}
                        </Typography>
                        {!!safeStr(item.meta).trim() && (
                          <Typography variant="caption" color="text.secondary">
                            {safeStr(item.meta)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

             
            </Grid>
          </Grid>
        )}
        </Box>
      </Box>
    </>
  );
};

export default CandidateProfileDetail;

